import { apiClient, isHttpClientError } from "@/http";
import type { JsonObject } from "./common";
import type { RegistryDatasetRef } from "./training";
import { apiRoutes } from "./apiRoutes";

export type TrainingBudgetUnit = "epoch" | "step";

// Policy 카탈로그. 체크포인트를 Policy 로 큐레이션한 1급 카탈로그다. actor 스코프로 read·등록·
// Policy-중심 평가를 한다. BE 가 스코프를 강제한다:
//   team = group 의 workspace 세션 전부(누가 돌렸든 팀 것)
//   me   = actor가 현재 선점한 workspace의 모든 세션에서 등록된 정책.
//          세션 실행자 신원은 판정하지 않는다.
// 세션 상세의 체크포인트(SessionCheckpointView)와 grain 이 다르다. 체크포인트는 세션이 만든
// epoch 이고, 이 타입은 artifact_uri 로 등록된 Policy 다.

// Policy source workspace 를 선점한 registry 멤버(derived). 선점되지 않은 workspace 면 null 이다.
export type TrainingPolicyOwnerView = {
  member_uuid: string;
  display_name: string;
};

// Policy 1행. 세션 lineage(session_display_name·experiment·workspace)는 BE 가 세션 맵 조인으로
// 채운다.
export type TrainingPolicyView = {
  id: string;
  name: string;
  source_session_id: string;
  epoch: number | null;
  budget_unit: TrainingBudgetUnit;
  source_session_total_epochs: number | null;
  source_session_total_steps: number | null;
  artifact_uri: string | null;
  created_at: string | null;
  session_display_name: string | null;
  experiment_id: string | null;
  experiment_name: string | null;
  workspace_id: string | null;
  owner_member: TrainingPolicyOwnerView | null;
  // 코드 출처 provenance. training 이 등록 시점에 비정규화한 learning-core 브랜치·커밋이다.
  // 같은 데이터셋에서 나온 정책들을 구분하는 축이다. 그래서 시작점을 고를 때 함께 읽는다.
  learning_core_ref: string | null;
  learning_core_sha: string | null;
  // 학습 입력 provenance. training 이 등록한 dataset id/version 이다(등록 시점 비정규화).
  dataset_id: string | null;
  dataset_version: string | null;
  // dataset_id 가 registry 등록 스냅샷과 매칭되면 원본 참조(표시명+링크)가 있다.
  // 원본 참조가 없으면 training-infra 전용 데이터셋으로 표시한다.
  dataset_registry_ref: RegistryDatasetRef | null;
  // 모든 train 입력이 Real 로 분류된 registry Dataset 으로 해석될 때만 true 다.
  trained_only_on_real_data: boolean;
  // training 이 정책 페이지마다 1쿼리로 집계해 보내는 eval-run 롤업. 상류가 아직 보내지 않으면
  // null 이다.
  eval_runs?: TrainingPolicyEvalRunsView | null;
};

// 정책별 eval-run 롤업:
//   - count: 정책 FK 가 기록된 run 전체(상태 무관)
//   - scored_count: succeeded 이고 metrics 를 수집한 run 수
//   - success_rate_mean: scored run 들의 success_rate(0~1)를 시나리오 전체에 걸쳐 단순 평균한 값.
//     scored_count 가 0 이면 null 이다.
export type TrainingPolicyEvalRunsView = {
  count: number;
  scored_count: number;
  success_rate_mean: number | null;
};

// 카탈로그 목록 필터(전부 선택 항목). from/to 는 created_at 범위(ISO datetime)다.
// 스코프 밖 값이면 빈 목록이 온다.
export type TrainingPolicyFilters = {
  experimentId?: string;
  from?: string;
  to?: string;
};

export type TrainingTeamPolicyFilters = TrainingPolicyFilters & {
  memberUuid?: string;
};

function toPolicyParams(filters: TrainingPolicyFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.experimentId) {
    params.experiment_id = filters.experimentId;
  }
  if (filters.from) {
    params.from = filters.from;
  }
  if (filters.to) {
    params.to = filters.to;
  }
  return params;
}

// 체크포인트를 정책으로 등록한다. source_session_id는 소유 범위를 확인하고 artifact_uri는
// 같은 아티팩트의 중복 등록을 막는 키로 사용한다.
export type RegisterPolicyReq = {
  name: string;
  artifact_uri: string;
  source_session_id: string;
  experiment_id?: string | null;
};

// 정책-중심 평가의 scenario 입력. 서버는 scenario×seed 마다 eval-run 1개를 만든다.
export type EvalScenarioInput = {
  name: string;
  benchmark_image?: string | null;
  params?: JsonObject;
};

export type EvaluatePolicyReq = {
  scenarios?: EvalScenarioInput[];
  seeds?: number[] | null;
  timeout_sec?: number | null;
  gpu_share_count?: number | null;
  reason?: string | null;
};

export type TrainingPolicyEvalRunView = {
  id: string;
  scenario: string | null;
  status: string;
  source_session_id: string | null;
  created_at: string | null;
};

// 평가 트리거 결과. 생성된 eval-run 들이다. 이후 eval 모니터로 연결한다.
export type TrainingPolicyEvaluateView = {
  policy_id: string;
  eval_group_id: string | null;
  seeds: number[];
  eval_runs: TrainingPolicyEvalRunView[];
};

// 팀(group) 스코프 정책 카탈로그. group_uuid 를 주면 그 그룹으로 좁힌다.
// 주지 않으면 actor 의 팀 전체를 집계한다.
export async function listTeamPolicies(
  filters: TrainingTeamPolicyFilters = {},
  groupUuid?: string,
): Promise<TrainingPolicyView[]> {
  const params = toPolicyParams(filters);
  if (filters.memberUuid) {
    params.member_uuid = filters.memberUuid;
  }
  if (groupUuid) {
    params.group_uuid = groupUuid;
  }
  const response = await apiClient.get<TrainingPolicyView[]>(apiRoutes.training.teamPolicies, {
    params,
  });
  return response.data;
}

// 개인(me) 스코프 정책 카탈로그.
export async function listMyPolicies(
  filters: TrainingPolicyFilters = {},
): Promise<TrainingPolicyView[]> {
  const response = await apiClient.get<TrainingPolicyView[]>(apiRoutes.training.mePolicies, {
    params: toPolicyParams(filters),
  });
  return response.data;
}

// Policy 카탈로그 표(페이지 슬라이싱)와 제출 위저드의 finetune 시작점 후보가 스코프 정책
// 카탈로그를 함께 쓴다. 페이지네이션이 없는 전체 목록이다. 그래서 탭을 바꿀 때마다 다시 받지
// 않도록 세션 캐시를 둔다(rules.md §7). 필터 조합마다 캐시 서명이 다르다.
export type ScopedPolicyQuery = {
  scope: "me" | "team";
  // Team 스코프 전용. 지정하지 않으면 actor 의 팀 전체를 집계한다.
  groupUuid?: string;
  // Team 스코프 전용. BE 가 선택한 멤버들이 선점한 workspace 의 합집합을 계산한다.
  memberUuid?: string;
  filters?: TrainingPolicyFilters;
};

// 필터 조합은 멤버·실험 캐스케이드로 정해진다. 그래서 조합 수는 적다.
const SCOPED_POLICY_MAX_ENTRIES = 8;
const scopedPolicies = new Map<string, Promise<readonly TrainingPolicyView[]>>();

function scopedPolicySignature({
  scope,
  groupUuid,
  memberUuid = "",
  filters = {},
}: ScopedPolicyQuery): string {
  const { experimentId = "", from = "", to = "" } = filters;
  return `${scope}|${groupUuid ?? ""}|${memberUuid}|${experimentId}|${from}|${to}`;
}

export function loadScopedPolicies(
  query: ScopedPolicyQuery,
): Promise<readonly TrainingPolicyView[]> {
  const signature = scopedPolicySignature(query);
  const cached = scopedPolicies.get(signature);
  if (cached !== undefined) {
    return cached;
  }
  const request =
    query.scope === "me"
      ? listMyPolicies(query.filters)
      : listTeamPolicies({ ...query.filters, memberUuid: query.memberUuid }, query.groupUuid);
  scopedPolicies.set(signature, request);
  while (scopedPolicies.size > SCOPED_POLICY_MAX_ENTRIES) {
    const oldest = scopedPolicies.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    scopedPolicies.delete(oldest);
  }
  // 실패는 캐시에 남기지 않는다. 그래서 다음 조회에서 다시 시도한다.
  request.catch(() => scopedPolicies.delete(signature));
  return request;
}

export function invalidateScopedPolicyCatalog(): void {
  scopedPolicies.clear();
}

// 단건 조회(결과 패널 Policy Info). 스코프 밖이면 404 다(leak 방지).
export async function getTrainingPolicy(policyId: string): Promise<TrainingPolicyView> {
  const response = await apiClient.get<TrainingPolicyView>(apiRoutes.training.policy(policyId));
  return response.data;
}

// 체크포인트를 정책으로 등록한다(201). 등록 정책에 lineage·owner 를 채운 view 를 반환한다.
export async function registerTrainingPolicy(
  payload: RegisterPolicyReq,
): Promise<TrainingPolicyView> {
  const response = await apiClient.post<TrainingPolicyView>(apiRoutes.training.policies, payload);
  // 새 정책을 스코프 카탈로그에 넣는다. 그래야 표와 provenance 폴백이 새 정책을 본다.
  invalidateScopedPolicyCatalog();
  return response.data;
}

// 요청자가 바로 받는 1시간 서명 URL. 공유 링크와 달리 발급 기록이 남지 않는다.
export type TrainingPolicyDownloadView = {
  download_url: string;
  filename: string;
  size_bytes: number | null;
  expires_at: string;
};

export async function getTrainingPolicyDownload(
  policyId: string,
): Promise<TrainingPolicyDownloadView> {
  const response = await apiClient.get<TrainingPolicyDownloadView>(
    apiRoutes.training.policyDownload(policyId),
  );
  return response.data;
}

// 정책 파일이 MLflow run 밖에 있어 서명할 수 없다(예: 세션 초기 파일로 올린 정책).
// 부재(404)와 달리 목록을 새로고침해도 결과가 같다. 다운로드·공유 둘 다 같은 코드를 쓴다.
export function isPolicyArtifactUnavailableError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.policy_artifact_unavailable";
}

// 정책-중심 평가(202). 서버는 scenario×seed 마다 eval-run 을 만든다.
export async function evaluateTrainingPolicy(
  policyId: string,
  payload: EvaluatePolicyReq,
): Promise<TrainingPolicyEvaluateView> {
  const response = await apiClient.post<TrainingPolicyEvaluateView>(
    apiRoutes.training.policyEvaluate(policyId),
    payload,
  );
  return response.data;
}
